import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable, { type Styles } from "jspdf-autotable";
import PptxGenJS from "pptxgenjs";

type AutoTablePdf = jsPDF & {
  lastAutoTable?: {
    finalY?: number;
  };
};

// Helper function to create chart canvas
const createChartCanvas = (value, p25, p50, p75) => {
  const canvas = document.createElement("canvas");
  const width = 400;
  const height = 80;
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");

  // Clear background
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);

  const padding = 20;
  const chartWidth = width - 2 * padding;
  const lineY = height / 2;

  // Fixed positions for percentile markers (matching website)
  const percentilePositions = {
    p25: 16.67,
    p50: 50,
    p75: 83.33,
  };

  const getXPos = (percent) => padding + (chartWidth * percent) / 100;

  // Draw horizontal base line
  ctx.strokeStyle = "#d1d5db";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(padding, lineY);
  ctx.lineTo(width - padding, lineY);
  ctx.stroke();

  // Draw percentile markers
  const drawMarker = (xPercent, label, valueText) => {
    const x = getXPos(xPercent);

    // Vertical line
    ctx.strokeStyle = "#9ca3af";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x, lineY - 8);
    ctx.lineTo(x, lineY + 8);
    ctx.stroke();

    // Label above
    ctx.fillStyle = "#6b7280";
    ctx.font = "11px Arial";
    ctx.textAlign = "center";
    ctx.fillText(label, x, lineY - 15);

    // Value below
    ctx.fillStyle = "#4b5563";
    ctx.font = "bold 11px Arial";
    ctx.fillText(valueText, x, lineY + 25);
  };

  drawMarker(percentilePositions.p25, "25th", `${p25.toFixed(1)}%`);
  drawMarker(percentilePositions.p50, "50th", `${p50.toFixed(1)}%`);
  drawMarker(percentilePositions.p75, "75th", `${p75.toFixed(1)}%`);

  // Calculate company value position
  let valuePosition;
  if (value <= p25) {
    const range = p25 - p25 * 0.8;
    const ratio = (value - p25 * 0.8) / range;
    valuePosition = ratio * percentilePositions.p25;
  } else if (value <= p50) {
    const ratio = (value - p25) / (p50 - p25);
    valuePosition =
      percentilePositions.p25 +
      ratio * (percentilePositions.p50 - percentilePositions.p25);
  } else if (value <= p75) {
    const ratio = (value - p50) / (p75 - p50);
    valuePosition =
      percentilePositions.p50 +
      ratio * (percentilePositions.p75 - percentilePositions.p50);
  } else {
    const range = p75 * 1.2 - p75;
    const ratio = (value - p75) / range;
    valuePosition =
      percentilePositions.p75 + ratio * (100 - percentilePositions.p75);
  }

  valuePosition = Math.max(0, Math.min(100, valuePosition));
  const valueX = getXPos(valuePosition);

  // Draw company value marker (green square)
  const markerSize = 12;
  ctx.fillStyle = "#86bc25";
  ctx.fillRect(
    valueX - markerSize / 2,
    lineY - markerSize / 2,
    markerSize,
    markerSize,
  );

  // Company value text below
  ctx.fillStyle = "#86bc25";
  ctx.font = "bold 12px Arial";
  ctx.textAlign = "center";
  ctx.fillText(`${value.toFixed(1)}%`, valueX, lineY + 35);

  return canvas;
};
const getStoredParameterSelections = () => {
  try {
    const raw = localStorage.getItem("parametersSelections");
    if (!raw) return null;
    console.log("Raw parametersSelections from localStorage:", raw);
    const parsed = JSON.parse(raw);
    console.log("Parsed parametersSelections:", parsed);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      industry: Array.isArray(parsed.selectedIndustry) ? parsed.selectedIndustry.join(", ") : "",
      sector: Array.isArray(parsed.selectedSector) ? parsed.selectedSector.join(", ") : "",
      companySizes: Array.isArray(parsed.selectedCompanySizes) ? parsed.selectedCompanySizes.join(", ") : "",
      ownershipType: Array.isArray(parsed.selectedOwnershipType) ? parsed.selectedOwnershipType.join(", ") : "",
      geography: Array.isArray(parsed.selectedGeography) ? parsed.selectedGeography.join(", ") : "",
    };
  } catch {
    return null;
  }
};

const getStoredCompanyIds = () => {
  try {
    const stored = localStorage.getItem("filteredCompanyIds");
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getStoredSelectedKpiIds = () => {
  try {
    const stored = localStorage.getItem("selectedKpiIds");
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed)
      ? parsed
          .map((id) => Number(id))
          .filter((id) => Number.isInteger(id) && id > 0)
      : [];
  } catch {
    return [];
  }
};

const getUploadedTempCompanyId = () => {
  try {
    const stored = localStorage.getItem("uploadedTempCompanyId");
    const parsed = Number(stored);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
};
// Helper to create detail chart canvas with optional percent formatting
const createDetailChartCanvas = (
  value,
  p25,
  p50,
  p75,
  formatAsPercent = false,
) => {
  const canvas = document.createElement("canvas");
  const width = 360;
  const height = 72;
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);

  const padding = 18;
  const chartWidth = width - 2 * padding;
  const lineY = height / 2;

  const percentilePositions = { p25: 16.67, p50: 50, p75: 83.33 };
  const getXPos = (percent) => padding + (chartWidth * percent) / 100;

  // baseline
  ctx.strokeStyle = "#d1d5db";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(padding, lineY);
  ctx.lineTo(width - padding, lineY);
  ctx.stroke();

  const fmt = (v) =>
    formatAsPercent ? `${v.toFixed(1)}%` : `${Math.round(v).toLocaleString()}`;

  const drawMarker = (xPercent, label, valueText) => {
    const x = getXPos(xPercent);
    ctx.strokeStyle = "#9ca3af";
    ctx.lineWidth = 1.25;
    ctx.beginPath();
    ctx.moveTo(x, lineY - 7);
    ctx.lineTo(x, lineY + 7);
    ctx.stroke();

    ctx.fillStyle = "#6b7280";
    ctx.font = "10px Arial";
    ctx.textAlign = "center";
    ctx.fillText(label, x, lineY - 13);

    ctx.fillStyle = "#4b5563";
    ctx.font = "bold 10px Arial";
    ctx.fillText(valueText, x, lineY + 22);
  };

  drawMarker(percentilePositions.p25, "25th", fmt(p25));
  drawMarker(percentilePositions.p50, "50th", fmt(p50));
  drawMarker(percentilePositions.p75, "75th", fmt(p75));

  // compute value position relative to percentiles
  let valuePosition;
  if (value <= p25) {
    const range = p25 - p25 * 0.8;
    const ratio = (value - p25 * 0.8) / (range || 1);
    valuePosition = ratio * percentilePositions.p25;
  } else if (value <= p50) {
    const ratio = (value - p25) / (p50 - p25 || 1);
    valuePosition =
      percentilePositions.p25 +
      ratio * (percentilePositions.p50 - percentilePositions.p25);
  } else if (value <= p75) {
    const ratio = (value - p50) / (p75 - p50 || 1);
    valuePosition =
      percentilePositions.p50 +
      ratio * (percentilePositions.p75 - percentilePositions.p50);
  } else {
    const range = p75 * 1.2 - p75;
    const ratio = (value - p75) / (range || 1);
    valuePosition =
      percentilePositions.p75 + ratio * (100 - percentilePositions.p75);
  }

  valuePosition = Math.max(0, Math.min(100, valuePosition));
  const valueX = getXPos(valuePosition);

  ctx.fillStyle = "#86bc25";
  const markerSize = 10;
  ctx.fillRect(
    valueX - markerSize / 2,
    lineY - markerSize / 2,
    markerSize,
    markerSize,
  );

  ctx.fillStyle = "#86bc25";
  ctx.font = "bold 11px Arial";
  ctx.textAlign = "center";
  ctx.fillText(fmt(value), valueX, lineY + 30);

  return canvas;
};

const API_BASE_URL = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

function percentile(data, p) {
  const sorted = [...data].sort((a, b) => a - b);
  const n = sorted.length;
  if (n === 0) return null;
  if (n === 1) return sorted[0];

  const index = (p / 100) * (n - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);

  if (lower === upper) return sorted[lower];

  const fraction = index - lower;
  return sorted[lower] + fraction * (sorted[upper] - sorted[lower]);
}

function getPercentiles(data, percentiles = [25, 50, 75]) {
  const result = {};
  percentiles.forEach((p) => {
    result[p] = percentile(data, p);
  });
  return result;
}

const HorizontalBenchmarkChart = ({
  compareCompanyView,
  calculatedData = [],
}) => {
  const chartRef = useRef(null);
  const hasTriggeredTempCalculationRef = useRef(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [observations, setObservations] = useState({});
  const [loadingObservations, setLoadingObservations] = useState({});
  const [storedCompanyIdsCount, setStoredCompanyIdsCount] = useState(
    () => getStoredCompanyIds().length,
  );
  // const hasCompareData = Array.isArray(data) && data.length > 0;
  const [tempData, setTempData] = useState([]);
  const [validCompaniesCount, setValidCompaniesCount] = useState(0);
  const [kpiData, setKpiData] = useState([]);
  const [kpiDataLoading, setKpiDataLoading] = useState(true);
  const [kpiDataError, setKpiDataError] = useState(null);

  useEffect(() => {
    const refreshStoredCompanyIds = () => {
      setStoredCompanyIdsCount(getStoredCompanyIds().length);
    };

    // Refresh immediately on mount so the latest localStorage value is used.
    refreshStoredCompanyIds();

    window.addEventListener("storage", refreshStoredCompanyIds);
    window.addEventListener(
      "filteredCompanyIdsUpdated",
      refreshStoredCompanyIds,
    );

    return () => {
      window.removeEventListener("storage", refreshStoredCompanyIds);
      window.removeEventListener(
        "filteredCompanyIdsUpdated",
        refreshStoredCompanyIds,
      );
    };
  }, []);

  useEffect(() => {
    console.log("Stored company IDs in localStorage:", storedCompanyIdsCount);
  }, [storedCompanyIdsCount]);

  // Fetch KPI data from API
  useEffect(() => {
    const fetchKpiData = async () => {
      setKpiDataLoading(true);
      setKpiDataError(null);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/kpi?include_sql_equivalent=true`,
        );

        // Transform API response to match expected format
        const transformedData = response.data.kpis.map((kpi) => ({
          id: kpi.id,
          function: kpi.Function,
          kpiCategory: kpi.KPI_Category,
          kpiName: kpi.KPI_Name,
        }));

        setKpiData(transformedData);
      } catch (error) {
        console.error("Error fetching KPI data from API:", error);
        setKpiDataError(error.message);
        setKpiData([]);
      } finally {
        setKpiDataLoading(false);
      }
    };

    fetchKpiData();
  }, []);

  useEffect(() => {
    if (!compareCompanyView || hasTriggeredTempCalculationRef.current) {
      return;
    }

    const uploadedTempCompanyId = getUploadedTempCompanyId();
    const selectedKpiIds = getStoredSelectedKpiIds();
    console.log(uploadedTempCompanyId, "uploadedTempCompanyId");
    if (!uploadedTempCompanyId || selectedKpiIds.length === 0) {
      return;
    }

    hasTriggeredTempCalculationRef.current = true;

    const triggerTempCalculationAndFetchValues = async () => {
      try {
        await axios.post(`${API_BASE_URL}/api/kpi/run_calculations`, {
          company_ids: [uploadedTempCompanyId],
          kpi_ids: selectedKpiIds,
          environment: "temp",
        });

        const response = await axios.get(
          `${API_BASE_URL}/api/kpi/calculated_values`,
          {
            params: {
              company_id: uploadedTempCompanyId,
              environment: "temp",
            },
          },
        );
        setTempData(response.data.rows || []);
      } catch (error) {
        hasTriggeredTempCalculationRef.current = false;
        console.error("Temp KPI calculation/calc-values request failed", error);
      }
    };

    triggerTempCalculationAndFetchValues();
  }, [compareCompanyView]);

  const companiesCount = (selectedRow) => {
    const company =
      calculatedData
        .filter((item) => item.kpi_name === selectedRow.kpi)
        .filter(
          (item) =>
            item.calculated_value !== null &&
            item.calculated_value !== undefined &&
            item.calculated_value !== 0,
        ).length || 0;
    return company;
  };
  console.log("Calculated data for benchmarking:", calculatedData);
  const selectedKpiRows = (() => {
    try {
      const stored = localStorage.getItem("selectedKpiRows");
      const parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : [];
    } catch {
      return [];
    }
  })();

  const selectedRowsForDisplay =
    selectedKpiRows.length > 0 ? selectedKpiRows : [];
  console.log("Selected KPI Rows for display:", selectedRowsForDisplay);

  const normalizeKpiText = (value) =>
    String(value || "")
      .toLowerCase()
      .trim();

  const getCalculatedValuesForKpi = (kpiName) =>
    calculatedData
      .filter(
        (item) =>
          normalizeKpiText(item.kpi_name) === normalizeKpiText(kpiName) &&
          item.calculated_value !== null &&
          item.calculated_value !== undefined &&
          Number.isFinite(Number(item.calculated_value)),
      )
      .map((item) => Number(item.calculated_value));

  const buildBenchmarkRowFromCalculatedData = (kpiName) => {
    const values = getCalculatedValuesForKpi(kpiName);

    if (values.length === 0) {
      return null;
    }

    const computed = getPercentiles(values, [25, 50, 75]);

    return {
      KPI: kpiName,
      Value: computed[50] ?? 0,
      "25th Percentile": computed[25] ?? 0,
      "50th Percentile": computed[50] ?? 0,
      "75th Percentile": computed[75] ?? 0,
    };
  };

  const getSelectedRowsForLever = (lever) =>
    selectedKpiRows.length > 0
      ? selectedRowsForDisplay.filter((selectedRow) =>
          kpiData.some(
            (kpi) =>
              kpi.function === lever &&
              kpi.kpiCategory === selectedRow.category &&
              kpi.kpiName === selectedRow.kpi,
          ),
        )
      : selectedRowsForDisplay;

  const getPlotRowForLever = (lever) => {
    const selectedRowsForLever = getSelectedRowsForLever(lever);

    return buildBenchmarkRowFromCalculatedData(selectedRowsForLever[0]?.kpi);
  };

  const getKpiRowForSelectedRow = (selectedRow) => {
    return buildBenchmarkRowFromCalculatedData(selectedRow.kpi);
  };

  const formatDisplayedPercentileValue = (value, formatAsPercent = false) => {
    const numericValue = Number(value);
    const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
    return `${safeValue.toFixed(1)}${formatAsPercent ? "%" : ""}`;
  };

  const getDisplayedBenchmarkExportRows = () =>
    allLevers
      .map((lever) => {
        const plotRow = getPlotRowForLever(lever);

        if (!plotRow) {
          return null;
        }

        const formatAsPercent = String(plotRow?.KPI || "").includes("%");

        return [
          lever,
          formatDisplayedPercentileValue(
            plotRow["25th Percentile"],
            formatAsPercent,
          ),
          formatDisplayedPercentileValue(
            plotRow["50th Percentile"],
            formatAsPercent,
          ),
          formatDisplayedPercentileValue(
            plotRow["75th Percentile"],
            formatAsPercent,
          ),
        ];
      })
      .filter(Boolean);

  const getDisplayedBenchmarkDetailSections = () =>
    allLevers
      .map((lever) => {
        const selectedRowsForLever = getSelectedRowsForLever(lever);
        const detailRows = selectedRowsForLever
          .map((selectedRow) => {
            const kpiRow = getKpiRowForSelectedRow(selectedRow);

            if (!kpiRow) {
              return null;
            }

            const companyCount = calculatedData
              .filter((item) => item.kpi_name === selectedRow.kpi)
              .filter(
                (item) =>
                  item.calculated_value !== null &&
                  item.calculated_value !== undefined &&
                  item.calculated_value !== 0,
              ).length || 0;

            return [
              selectedRow.category,
              selectedRow.kpi,
              companyCount,
              formatDisplayedPercentileValue(
                kpiRow["25th Percentile"],
                String(selectedRow.kpi || "").includes("%"),
              ),
              formatDisplayedPercentileValue(
                kpiRow["50th Percentile"],
                String(selectedRow.kpi || "").includes("%"),
              ),
              formatDisplayedPercentileValue(
                kpiRow["75th Percentile"],
                String(selectedRow.kpi || "").includes("%"),
              ),
              compareCompanyView
                ? observations[`${lever}-${selectedRow.kpi}`] || "-"
                : "-",
            ];
          })
          .filter(Boolean);

        return detailRows.length > 0
          ? {
              lever,
              detailRows,
            }
          : null;
      })
      .filter(Boolean);

  // Function to generate observation for a KPI
  const generateObservation = async (
    company,
    valueLever,
    kpi,
    value,
    p25,
    p50,
    p75,
  ) => {
    const key = `${valueLever}-${kpi}`;

    // Skip if already loaded or loading
    if (observations[key] || loadingObservations[key]) {
      return;
    }

    setLoadingObservations((prev) => ({ ...prev, [key]: true }));

    try {
      const response = await axios.post(
        `${API_BASE_URL}/generate-observation`,
        {
          company: company,
          value_lever: valueLever,
          kpi: kpi,
          value: value,
          p25: p25,
          p50: p50,
          p75: p75,
        },
      );

      setObservations((prev) => ({
        ...prev,
        [key]: response.data.observation,
      }));
    } catch (error) {
      console.error("Error generating observation:", error);
      setObservations((prev) => ({
        ...prev,
        [key]: "Unable to generate observation",
      }));
    } finally {
      setLoadingObservations((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Get company name from data
  const getCompanyName = () => {
    if (compareCompanyView) {
      return (
        localStorage.getItem("uploadedTempCompanyName") || "Selected Company"
      );
    }
    return "Benchmark";
  };

  const getTempCalculatedValue = (kpi) => {
    const tempKpi = tempData.find((item) => item.kpi_name === kpi);
    return tempKpi ? Number(tempKpi.calculated_value) : null;
  };

  const toggleRow = (lever) => {
    setExpandedRows((prev) => ({
      ...prev,
      [lever]: !prev[lever],
    }));
  };

  const toggleAllRows = (levers) => {
    const allExpanded = levers.every((lever) => expandedRows[lever]);
    const newExpandedState = {};
    levers.forEach((lever) => {
      newExpandedState[lever] = !allExpanded;
    });
    setExpandedRows(newExpandedState);
  };
  //   const benchmarkRows = getDisplayedBenchmarkExportRows();

  //   if (benchmarkRows.length === 0) {
  //     alert("No benchmark data available to export");
  //     return;
  //   }

  //   try {
  //     const pdf = new jsPDF({
  //       orientation: "landscape",
  //       unit: "mm",
  //       format: "a4",
  //     }) as AutoTablePdf;
  //     const pdfWidth = pdf.internal.pageSize.getWidth();
  //     const margin = 15;
  //     const companyName = getCompanyName();
  //     const dateStr = new Date().toISOString().split("T")[0];

  //     // Header
  //     pdf.setFont("helvetica", "bold");
  //     pdf.setFontSize(16);
  //     pdf.text("Benchmark Data Table", pdfWidth / 2, 18, { align: "center" });
  //     pdf.setFont("helvetica", "normal");
  //     pdf.setFontSize(11);
  //     pdf.text(`${companyName} — ${dateStr}`, pdfWidth / 2, 26, {
  //       align: "center",
  //     });

  //     const params = getStoredParameterSelections();
  //     let filtersStartY = 34;
  //     if (params) {
  //       const filterRows = [
  //         ["Industry", params.industry || "-"],
  //         ["Sector", params.sector || "-"],
  //         ["Company Size", params.companySizes || "-"],
  //         ["Geography", params.geography || "-"],
  //         ["Ownership Type", params.ownershipType || "-"],
  //       ];
  //       pdf.setFont("helvetica", "bold");
  //       pdf.setFontSize(11);
  //       pdf.text("Filters Applied", margin, 34);
  //       autoTable(pdf, {
  //         startY: 38,
  //         head: [["Parameter", "Selected Values"]],
  //         body: filterRows,
  //         theme: "grid",
  //         headStyles: { fillColor: [0, 102, 68], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9 },
  //         bodyStyles: { fontSize: 8.5, cellPadding: 2 },
  //         columnStyles: { 0: { cellWidth: 45, fontStyle: "bold" }, 1: { cellWidth: "auto" } },
  //         margin: { left: margin, right: margin },
  //       });
  //       filtersStartY = (pdf.lastAutoTable?.finalY || 38) + 8;
  //     }

  //     const head = [
  //       ["Function", "25th Percentile", "50th Percentile", "75th Percentile"],
  //     ];

  //     const body = benchmarkRows;

  //     autoTable(pdf, {
  //       startY: filtersStartY,
  //       head,
  //       body,
  //       theme: "grid",
  //       headStyles: {
  //         fillColor: [0, 51, 102],
  //         textColor: [255, 255, 255],
  //         fontStyle: "bold",
  //         halign: "center",
  //         fontSize: 9.5,
  //       },
  //       bodyStyles: {
  //         fontSize: 8.5,
  //         cellPadding: 2.5,
  //       },
  //       styles: { overflow: "linebreak" },
  //       columnStyles: head[0].reduce((acc, key, idx) => {
  //         acc[idx] = {
  //           cellWidth: "auto",
  //           halign: idx <= 1 ? "left" : "center",
  //         };
  //         return acc;
  //       }, {}),
  //       margin: { left: margin, right: margin },
  //     });

  //     const filename = `${companyName.replace(/\s+/g, "_")}_Benchmark_Data_${dateStr}.pdf`;
  //     pdf.save(filename);
  //   } catch (err) {
  //     console.error("Export Benchmarks PDF failed:", err);
  //     alert("Export Benchmarks failed. Please try again.");
  //   }
  // };

  const exportToPDF = async () => {
    const summaryRows = getDisplayedBenchmarkExportRows();
    const detailSections = getDisplayedBenchmarkDetailSections();

    if (summaryRows.length === 0) {
      alert("No benchmark data available to export");
      return;
    }

    try {
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      }) as AutoTablePdf;

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const margin = 15;
      const companyName = getCompanyName();
      const dateStr = new Date().toISOString().split("T")[0];

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(16);
      pdf.text("Benchmark Report", pdfWidth / 2, 18, { align: "center" });
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(11);
      pdf.text(`${companyName} — ${dateStr}`, pdfWidth / 2, 26, {
        align: "center",
      });

      const reportParams = getStoredParameterSelections();
      let reportFiltersEndY = 34;
      if (reportParams) {
        const filterRows = [
          ["Industry", reportParams.industry || "-"],
          ["Sector", reportParams.sector || "-"],
          ["Company Size", reportParams.companySizes || "-"],
          ["Geography", reportParams.geography || "-"],
          ["Ownership Type", reportParams.ownershipType || "-"],
        ];
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11);
        pdf.text("Filters Applied", margin, 34);
        autoTable(pdf, {
          startY: 38,
          head: [["Parameter", "Selected Values"]],
          body: filterRows,
          theme: "grid",
          headStyles: { fillColor: [0, 102, 68], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9 },
          bodyStyles: { fontSize: 8.5, cellPadding: 2 },
          columnStyles: { 0: { cellWidth: 45, fontStyle: "bold" }, 1: { cellWidth: "auto" } },
          margin: { left: margin, right: margin },
        });
        reportFiltersEndY = (pdf.lastAutoTable?.finalY || 38) + 8;
      }
      //   startY: reportFiltersEndY,
      //   head: [
      //     ["Function", "25th Percentile", "50th Percentile", "75th Percentile"],
      //   ],
      //   body: summaryRows,
      //   theme: "grid",
      //   headStyles: {
      //     fillColor: [0, 51, 102],
      //     textColor: [255, 255, 255],
      //     fontStyle: "bold",
      //     halign: "center",
      //     fontSize: 9.5,
      //   },
      //   bodyStyles: {
      //     fontSize: 8.5,
      //     cellPadding: 2.5,
      //   },
      //   columnStyles: {
      //     0: { halign: "left", cellWidth: 65 },
      //     1: { halign: "center", cellWidth: 38 },
      //     2: { halign: "center", cellWidth: 38 },
      //     3: { halign: "center", cellWidth: 38 },
      //   },
      //   margin: { left: margin, right: margin },
      // });

      let nextY = (pdf.lastAutoTable?.finalY || 34) + 10;

      detailSections.forEach((section, index) => {
        if (nextY > 180) {
          pdf.addPage();
          nextY = 20;
        }

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(12);
        pdf.text(section.lever, margin, nextY);

        const detailHead = compareCompanyView
          ? [
              [
                "KPI Category",
                "KPI",
                "N",
                "25th",
                "50th",
                "75th",
                "Observations",
              ],
            ]
          : [["KPI Category", "KPI", "N", "25th", "50th", "75th"]];

        const detailBody = compareCompanyView
          ? section.detailRows
          : section.detailRows.map((row) => row.slice(0, 6));

        const detailColumnStyles: { [key: string]: Partial<Styles> } =
          compareCompanyView
          ? {
              0: { cellWidth: 48, halign: "left" },
              1: { cellWidth: 32, halign: "center" },
              2: { cellWidth: 16, halign: "center" },
              3: { cellWidth: 22, halign: "center" },
              4: { cellWidth: 22, halign: "center" },
              5: { cellWidth: 22, halign: "center" },
              6: { cellWidth: 70, halign: "left" },
            }
          : {
              0: { cellWidth: 58, halign: "left" },
              1: { cellWidth: 38, halign: "center" },
              2: { cellWidth: 18, halign: "center" },
              3: { cellWidth: 26, halign: "center" },
              4: { cellWidth: 26, halign: "center" },
              5: { cellWidth: 26, halign: "center" },
            };

        autoTable(pdf, {
          startY: nextY + 4,
          head: detailHead,
          body: detailBody,
          theme: "grid",
          headStyles: {
            fillColor: [134, 188, 37],
            textColor: [255, 255, 255],
            fontStyle: "bold",
            halign: "center",
            fontSize: 8.5,
          },
          bodyStyles: {
            fontSize: 8,
            cellPadding: 2,
            valign: "middle",
          },
          columnStyles: detailColumnStyles,
          margin: { left: margin, right: margin },
        });

        nextY =
          (pdf.lastAutoTable?.finalY || nextY + 4) +
          (index < detailSections.length - 1 ? 8 : 0);
      });

      pdf.save(
        `${companyName.replace(/\s+/g, "_")}_Benchmark_Report_${dateStr}.pdf`,
      );
    } catch (error) {
      console.error("PDF generation encountered an issue:", error);
    }
  };

  const exportToPPT = async () => {
    const summaryRows = getDisplayedBenchmarkExportRows();
    const detailSections = getDisplayedBenchmarkDetailSections();

    if (summaryRows.length === 0) {
      alert("No benchmark data available to export");
      return;
    }

    try {
      const prs = new PptxGenJS();
      prs.layout = "LAYOUT_WIDE";
      const companyName = getCompanyName();
      const dateStr = new Date().toISOString().split("T")[0];
      const GREEN = "006644";
      const BLUE = "003366";
      const LIME = "86BC25";

      // Slide 1: Title
      const titleSlide = prs.addSlide();
      titleSlide.background = { color: "FFFFFF" };
      titleSlide.addText("Benchmark Report", {
        x: 0.5, y: 2.2, w: "90%", h: 1.2,
        fontSize: 40, bold: true, color: GREEN, align: "center",
      });
      titleSlide.addText(`${companyName} — ${dateStr}`, {
        x: 0.5, y: 3.6, w: "90%", h: 0.6,
        fontSize: 20, color: "444444", align: "center",
      });

      // Slide 2: Filters Applied
      const reportParams = getStoredParameterSelections();
      if (reportParams) {
        const filterSlide = prs.addSlide();
        filterSlide.addText("Filters Applied", {
          x: 0.4, y: 0.3, w: "90%", h: 0.55, fontSize: 22, bold: true, color: GREEN,
        });
        const filterRows = [
          [
            { text: "Parameter", options: { bold: true, color: "FFFFFF", fill: { color: GREEN } } },
            { text: "Selected Values", options: { bold: true, color: "FFFFFF", fill: { color: GREEN } } },
          ],
          ["Industry", reportParams.industry || "-"],
          ["Sector", reportParams.sector || "-"],
          ["Company Size", reportParams.companySizes || "-"],
          ["Geography", reportParams.geography || "-"],
          ["Ownership Type", reportParams.ownershipType || "-"],
        ];
        filterSlide.addTable(filterRows as PptxGenJS.TableRow[], {
          x: 0.4, y: 1.0, w: 9, colW: [3.5, 5.5],
          fontSize: 13,
          border: { type: "solid", pt: 1, color: "CCCCCC" },
          rowH: 0.45,
        });
      }

     

      // All detail sections in one slide
      const detailSlide = prs.addSlide();
      detailSlide.addText("Detailed Breakdown by Function", {
        x: 0.4, y: 0.3, w: "90%", h: 0.55, fontSize: 22, bold: true, color: LIME,
      });

      const isCompare = compareCompanyView;
      const detailHeader = isCompare
        ? [
            { text: "Function", options: { bold: true, color: "FFFFFF", fill: { color: LIME } } },
            { text: "KPI Category", options: { bold: true, color: "FFFFFF", fill: { color: LIME } } },
            { text: "KPI",          options: { bold: true, color: "FFFFFF", fill: { color: LIME }, align: "center" as const } },
            { text: "N",            options: { bold: true, color: "FFFFFF", fill: { color: LIME }, align: "center" as const } },
            { text: "25th",         options: { bold: true, color: "FFFFFF", fill: { color: LIME }, align: "center" as const } },
            { text: "50th",         options: { bold: true, color: "FFFFFF", fill: { color: LIME }, align: "center" as const } },
            { text: "75th",         options: { bold: true, color: "FFFFFF", fill: { color: LIME }, align: "center" as const } },
            { text: "Observations", options: { bold: true, color: "FFFFFF", fill: { color: LIME } } },
          ]
        : [
            { text: "Function", options: { bold: true, color: "FFFFFF", fill: { color: LIME } } },
            { text: "KPI Category", options: { bold: true, color: "FFFFFF", fill: { color: LIME } } },
            { text: "KPI",          options: { bold: true, color: "FFFFFF", fill: { color: LIME }, align: "center" as const } },
            { text: "N",            options: { bold: true, color: "FFFFFF", fill: { color: LIME }, align: "center" as const } },
            { text: "25th",         options: { bold: true, color: "FFFFFF", fill: { color: LIME }, align: "center" as const } },
            { text: "50th",         options: { bold: true, color: "FFFFFF", fill: { color: LIME }, align: "center" as const } },
            { text: "75th",         options: { bold: true, color: "FFFFFF", fill: { color: LIME }, align: "center" as const } },
          ];

      const allDetailDataRows = detailSections.flatMap((section) => {
        const rawRows = isCompare ? section.detailRows : section.detailRows.map((row) => row.slice(0, 6));
        return rawRows.map((row, idx) => [
          idx === 0 ? section.lever : "",
          ...row.map((cell, i) => ({
            text: String(cell ?? ""),
            options: { align: (i === 0 || (isCompare && i === 6) ? "left" : "center") as "left" | "center" },
          })),
        ]);
      });

      const colW = isCompare
        ? [2.0, 2.5, 2.0, 0.6, 1.2, 1.2, 1.2, 2.8]
        : [2.0, 2.8, 2.0, 0.6, 1.5, 1.5, 1.6];

      detailSlide.addTable([detailHeader, ...allDetailDataRows] as PptxGenJS.TableRow[], {
        x: 0.4, y: 1.0, w: 12.5, colW,
        fontSize: 9,
        border: { type: "solid", pt: 1, color: "CCCCCC" },
        rowH: 0.32,
        autoPage: true,
        autoPageRepeatHeader: true,
      });

      await prs.writeFile({
        fileName: `${companyName.replace(/\s+/g, "_")}_Benchmark_Report_${dateStr}.pptx`,
      });
    } catch (error) {
      console.error("PPT generation encountered an issue:", error);
      alert("PPT export failed. Please try again.");
    }
  };

  const renderDetailChart = (
    row,
    formatAsPercent = false,
    kpiName = null,
    tempData = [],
    companyView = compareCompanyView,
    companyCount,
  ) => {
    const CompareValue = tempData.filter((d) => d.kpi_name === kpiName);
    const value = parseFloat(CompareValue[0]?.calculated_value) || 0;
    // Compute percentile markers only from getPercentiles
    let p25, p50, p75;
    const values = calculatedData
      .filter(
        (item) =>
          item.calculated_value !== null &&
          item.calculated_value !== undefined &&
          Number.isFinite(Number(item.calculated_value)) &&
          (!kpiName ||
            normalizeKpiText(item.kpi_name) === normalizeKpiText(kpiName)),
      )
      .map((item) => Number(item.calculated_value));
    console.log("Values used for percentiles:", values);

    const computed =
      values.length > 0
        ? getPercentiles(values, [25, 50, 75])
        : { 25: 0, 50: 0, 75: 0 };
    p25 = computed[25] ?? 0;
    p50 = computed[50] ?? 0;
    p75 = computed[75] ?? 0;
    console.log(value, "value");
    const showPercentSuffix = String(
      kpiName || row?.KPI || row?.kpi || "",
    ).includes("%");
    const formatValue = (val) => {
      const num = Number(val);
      if (!Number.isFinite(num)) {
        return showPercentSuffix ? "0.0%" : "0.0";
      }

      return `${num.toFixed(1)}${showPercentSuffix ? "%" : ""}`;
    };
    if (companyCount === 0)
      return (
        <div className="text-sm text-black-500 dark:text-gray-400">
          Benchmark values for this KPI can not be calculated as N=0.{" "}
          {companyView
            ? `However, the KPI value for the uploaded company is ${formatValue(value)}`
            : ``}
        </div>
      );

    console.log("ccc", compareCompanyView);

    // Fixed positions for percentile markers (aligned with columns)
    const percentilePositions = {
      p25: 16.67, // Center of first column
      p50: 50, // Center of second column
      p75: 83.33, // Center of third column
    };

    // Calculate company value position based on which percentile range it falls in
    let valuePosition;

    if (value <= p25) {
      // Below 25th percentile - proportional to left of first marker
      const range = p25 - p25 * 0.8;
      const ratio = (value - p25 * 0.8) / range;
      valuePosition = ratio * percentilePositions.p25;
    } else if (value <= p50) {
      // Between 25th and 50th percentile
      const ratio = (value - p25) / (p50 - p25);
      valuePosition =
        percentilePositions.p25 +
        ratio * (percentilePositions.p50 - percentilePositions.p25);
    } else if (value <= p75) {
      // Between 50th and 75th percentile
      const ratio = (value - p50) / (p75 - p50);
      valuePosition =
        percentilePositions.p50 +
        ratio * (percentilePositions.p75 - percentilePositions.p50);
    } else {
      // Above 75th percentile - proportional to right of last marker
      const range = p75 * 1.2 - p75;
      const ratio = (value - p75) / range;
      valuePosition =
        percentilePositions.p75 + ratio * (100 - percentilePositions.p75);
    }

    // Constrain to chart bounds
    valuePosition = Math.max(0, Math.min(100, valuePosition));

    return (
      <div className="relative h-12 w-full px-2">
        <div className="relative h-full flex items-center">
          {/* Horizontal base line */}
          <div
            className="absolute w-full h-0.5 bg-emerald-400 dark:bg-emerald-500"
            style={{ top: "50%" }}
          ></div>

          {/* Percentile markers at fixed positions */}
          <div
            className="absolute"
            style={{
              left: `${percentilePositions.p25}%`,
              transform: "translateX(-50%)",
            }}
          >
            <div className="text-xs text-blue-700 dark:text-blue-300 mb-1 whitespace-nowrap font-semibold bg-blue-50 dark:bg-blue-950/40 px-1.5 py-0.5 rounded">
              {formatValue(p25)}
            </div>
            <div className="w-0.5 h-3 bg-blue-500 dark:bg-blue-400 mx-auto"></div>
          </div>

          <div
            className="absolute"
            style={{
              left: `${percentilePositions.p50}%`,
              transform: "translateX(-50%)",
            }}
          >
            <div className="text-xs text-amber-700 dark:text-amber-300 mb-1 whitespace-nowrap font-semibold bg-amber-50 dark:bg-amber-950/40 px-1.5 py-0.5 rounded">
              {formatValue(p50)}
            </div>
            <div className="w-0.5 h-3 bg-amber-500 dark:bg-amber-400 mx-auto"></div>
          </div>

          <div
            className="absolute"
            style={{
              left: `${percentilePositions.p75}%`,
              transform: "translateX(-50%)",
            }}
          >
            <div className="text-xs text-fuchsia-700 dark:text-fuchsia-300 mb-1 whitespace-nowrap font-semibold bg-fuchsia-50 dark:bg-fuchsia-950/40 px-1.5 py-0.5 rounded">
              {formatValue(p75)}
            </div>
            <div className="w-0.5 h-3 bg-fuchsia-500 dark:bg-fuchsia-400 mx-auto"></div>
          </div>

          {/* Company value marker at calculated position */}
          {compareCompanyView && (
            <div
              className="absolute"
              style={{
                left: `${valuePosition}%`,
                transform: "translateX(-50%)",
                top: "50%",
              }}
            >
              <div
                className="w-2.5 h-2.5 bg-primary rounded-sm mx-auto"
                style={{ transform: "translateY(-50%)" }}
              ></div>
              <div className="text-xs font-semibold text-primary whitespace-nowrap mt-2">
                {formatValue(value)}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Get allowed functions based on selected KPIs
  const getAllowedFunctions = () => {
    if (selectedKpiRows.length === 0) {
      return new Set();
    }

    const allowedFunctions = new Set();

    selectedKpiRows.forEach((selectedRow) => {
      // Find all KPIs that match the selected category and kpi name
      kpiData.forEach((kpi) => {
        if (
          kpi.kpiCategory === selectedRow.category &&
          kpi.kpiName === selectedRow.kpi
        ) {
          allowedFunctions.add(kpi.function);
        }
      });
    });

    return allowedFunctions;
  };

  const allowedFunctions = getAllowedFunctions();

  const selectedFunctions =
    selectedKpiRows.length > 0
      ? Array.from(
          new Set(
            selectedRowsForDisplay.flatMap((selectedRow) =>
              kpiData
                .filter(
                  (kpi) =>
                    kpi.kpiCategory === selectedRow.category &&
                    kpi.kpiName === selectedRow.kpi,
                )
                .map((kpi) => kpi.function),
            ),
          ),
        )
      : [];

  // data.forEach((row) => {
  //   const lever = row["Value Lever"] || row.ValueLever || "Other";

  //   if (selectedKpiRows.length > 0 && !allowedFunctions.has(lever)) {
  //     return;
  //   }

  //   const kpi = normalizeKpiText(row.KPI || row.kpi || "");

  //   if (!groupedData[lever]) {
  //     groupedData[lever] = {
  //       function: lever,
  //       rows: [],
  //       hc: null,
  //       totalCost: null,
  //       avgComp: null,
  //       revenueRow: null,
  //       totalCostRow: null,
  //       hcRow: null,
  //       avgCompRow: null,
  //     };
  //   }
  //   groupedData[lever].rows.push(row);

  //   if (
  //     kpi === "hc" ||
  //     (kpi.includes("hc") &&
  //       !kpi.includes("revenue") &&
  //       !kpi.includes("cost") &&
  //       !kpi.includes("avg"))
  //   ) {
  //     groupedData[lever].hc = parseFloat(row.Value) || 0;
  //     groupedData[lever].hcRow = row;
  //   }

  //   if (
  //     kpi === "total cost" ||
  //     (kpi.includes("total cost") &&
  //       !kpi.includes("revenue") &&
  //       !kpi.includes("%"))
  //   ) {
  //     groupedData[lever].totalCost = parseFloat(row.Value) || 0;
  //     groupedData[lever].totalCostRow = row;
  //   }

  //   if (kpi.includes("avg comp") || kpi.includes("average comp")) {
  //     groupedData[lever].avgComp = parseFloat(row.Value) || 0;
  //     groupedData[lever].avgCompRow = row;
  //   }

  //   if (kpi.includes("total cost") && kpi.includes("revenue")) {
  //     groupedData[lever].revenueRow = row;
  //   }
  // });

  // Calculate totals
  // const calculateTotals = () => {
  //   let totalHC = 0;
  //   let totalCost = 0;
  //   let totalRevenue = 0;

  //   Object.values(groupedData).forEach((leverData) => {
  //     if (leverData.hc !== null && !isNaN(leverData.hc)) {
  //       totalHC += leverData.hc;
  //     }
  //     if (leverData.totalCost !== null && !isNaN(leverData.totalCost)) {
  //       totalCost += leverData.totalCost;
  //     }
  //     if (leverData.revenueRow) {
  //       const value = parseFloat(leverData.revenueRow.Value) || 0;
  //       totalRevenue += value;
  //     }
  //   });

  //   return {
  //     hc: totalHC,
  //     cost: totalCost,
  //     revenue: totalRevenue,
  //   };
  // };

  const allLevers = selectedFunctions.length > 0 ? selectedFunctions : [];

  const allExpanded = allLevers.every((lever) => expandedRows[lever]);

  return (
    <div
      className="bg-white dark:bg-gray-950 rounded-xl shadow-lg p-6 mb-8"
      ref={chartRef}
    >
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-2xl font-extrabold text-emerald-800 dark:text-emerald-200">
          Benchmarking Summary
        </h3>
        <div className="flex items-center gap-4">
          {compareCompanyView && (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-primary rounded-sm"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Company Value
                </span>
              </div>
            </div>
          )}
          <button
            className="flex items-center gap-2 px-4 py-2 bg-sky-100 dark:bg-sky-900/40 text-sky-800 dark:text-sky-200 rounded-lg hover:bg-sky-200 dark:hover:bg-sky-800/50 transition-colors font-medium text-sm border border-sky-200 dark:border-sky-800"
            onClick={() => toggleAllRows(allLevers)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {allExpanded ? (
                <>
                  <polyline points="18 15 12 9 6 15"></polyline>
                </>
              ) : (
                <>
                  <polyline points="6 9 12 15 18 9"></polyline>
                </>
              )}
            </svg>
            {allExpanded ? "Collapse All" : "Expand All"}
          </button>
          <>
            <button
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium text-sm shadow-md"
              onClick={exportToPDF}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              Export to PDF
            </button>
            <button
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium text-sm shadow-md"
              onClick={exportToPPT}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                <line x1="8" y1="21" x2="16" y2="21"></line>
                <line x1="12" y1="17" x2="12" y2="21"></line>
              </svg>
              Export to PPT
            </button>
          </>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="benchmark-table w-full border-collapse">
          <thead>
            <tr className="bg-emerald-100 dark:bg-gray-800 border-b-2 border-emerald-200 dark:border-gray-600">
              <th
                className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white border-r border-gray-300 dark:border-gray-600"
                rowSpan={2}
              >
                Function
              </th>
              <th
                className="px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white"
                colSpan={3}
              >
                <div className="inline-flex w-full items-center justify-center gap-1">
                  <span>Benchmarks</span>
                  <span className="group relative inline-flex cursor-help items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-black-400 transition-colors group-hover:text-emerald-500 dark:text-gray-500 dark:group-hover:text-emerald-400"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v-4" />
                      <path d="M12 8h.01" />
                    </svg>
                    <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 hidden w-64 -translate-x-1/2 rounded-xl border border-emerald-200 bg-white px-3 py-2 text-left text-[11px] font-medium leading-4 text-gray-700 shadow-[0_12px_32px_rgba(15,23,42,0.14)] group-hover:block dark:border-emerald-900/60 dark:bg-gray-900 dark:text-gray-200">
                     Percentiles show where this KPI sits across the selected peer group based on your filters.
                    </span>
                  </span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {allLevers.map((lever) => {

              const selectedRowsForLever = getSelectedRowsForLever(lever);

              const plotRow = getPlotRowForLever(lever);
              const isExpanded = expandedRows[lever];

              // Check if there's valid data to expand
              const hasExpandableData = selectedRowsForLever.length > 0;

              return (
                <React.Fragment key={lever}>
                  <tr className="border-b border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="px-4 py-3 align-middle">
                      <div className="flex items-center gap-2">
                        {hasExpandableData && (
                          <button
                            className="p-1 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                            onClick={() => toggleRow(lever)}
                            aria-label="Expand details"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <circle cx="11" cy="11" r="8"></circle>
                              <line
                                x1="21"
                                y1="21"
                                x2="16.65"
                                y2="16.65"
                              ></line>
                              <line x1="11" y1="8" x2="11" y2="14"></line>
                              <line x1="8" y1="11" x2="14" y2="11"></line>
                            </svg>
                          </button>
                        )}
                        <span className="font-medium text-gray-900 dark:text-white">
                          {lever}
                        </span>
                      </div>
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr className="bg-gray-50 dark:bg-gray-800">
                      <td colSpan={4} className="px-0 py-0">
                        <div className="p-6">
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead>
                                <tr className="border-b border-gray-300 dark:border-gray-600">
                                  <th
                                    className="px-3 py-2 text-left text-xs font-semibold text-gray-900 dark:text-white w-[120px] border-r border-gray-200 dark:border-gray-600"
                                    rowSpan={2}
                                  >
                                    KPI Category
                                  </th>
                                  <th
                                    className="px-3 py-2 text-center text-xs font-semibold text-gray-900 dark:text-white w-[80px] border-r border-gray-200 dark:border-gray-600"
                                    rowSpan={2}
                                  >
                                    KPI
                                  </th>
                                  <th
                                    className="px-3 py-2 text-center text-xs font-semibold text-gray-900 dark:text-white w-[80px] border-r border-gray-200 dark:border-gray-600"
                                    rowSpan={2}
                                  >
                                    <span className="inline-flex items-center justify-center gap-1">
                                      <span>N</span>
                                      <span className="group relative inline-flex cursor-help items-center">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="14"
                                          height="14"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="text-black-400 transition-colors group-hover:text-emerald-500 dark:text-gray-500 dark:group-hover:text-emerald-400"
                                          aria-hidden="true"
                                        >
                                          <circle cx="12" cy="12" r="10" />
                                          <path d="M12 16v-4" />
                                          <path d="M12 8h.01" />
                                        </svg>
                                        <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 hidden w-64 -translate-x-1/2 rounded-xl border border-emerald-200 bg-white px-3 py-2 text-left text-[11px] font-medium leading-4 text-gray-700 shadow-[0_12px_32px_rgba(15,23,42,0.14)] group-hover:block dark:border-emerald-900/60 dark:bg-gray-900 dark:text-gray-200">
                                          N = number of companies that have
                                          non-zero KPI values after applying
                                          parameter filters.
                                        </span>
                                      </span>
                                    </span>
                                  </th>
                                  <th
                                    className="px-3 py-2 text-center text-xs font-semibold text-gray-900 dark:text-white min-w-[300px] border-r border-gray-200 dark:border-gray-600"
                                    colSpan={3}
                                  >
                                    Benchmarks
                                  </th>
                                  {compareCompanyView && (
                                    <th
                                      className="px-3 py-2 text-left text-xs font-semibold text-gray-900 dark:text-white w-[250px]"
                                      rowSpan={2}
                                    >
                                      Observations
                                    </th>
                                  )}
                                </tr>
                                <tr className="border-b border-gray-200 dark:border-gray-600 bg-white/60 dark:bg-gray-800/40">
                                  <th className="px-2 py-1 text-center text-xs text-blue-700 dark:text-blue-300 border-l border-gray-200 dark:border-gray-600 font-semibold">
                                    25<sup>th</sup> Percentile
                                  </th>
                                  <th className="px-2 py-1 text-center text-xs text-amber-700 dark:text-amber-300 font-semibold">
                                    50<sup>th</sup> Percentile
                                  </th>
                                  <th className="px-2 py-1 text-center text-xs text-fuchsia-700 dark:text-fuchsia-300 border-r border-gray-200 dark:border-gray-600 font-semibold">
                                    75<sup>th</sup> Percentile
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedRowsForLever.map(
                                  (selectedRow, rowIndex) => {
                                    const kpiRow = getKpiRowForSelectedRow(
                                      selectedRow,
                                    );

                                    const companyCount =
                                      companiesCount(selectedRow);

                                    return (
                                      <tr
                                        key={`${lever}-${selectedRow.category}-${selectedRow.kpi}-${rowIndex}`}
                                        className="border-b border-gray-200 dark:border-gray-600"
                                      >
                                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-600">
                                          {selectedRow.category}
                                        </td>
                                        <td className="px-3 py-2 text-center text-sm font-medium text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-600">
                                          {selectedRow.kpi}
                                        </td>
                                        <td className="px-3 py-2 text-center text-sm font-medium text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-600">
                                          {companyCount}
                                        </td>
                                        <td
                                          className="px-3 py-2 border-r border-gray-200 dark:border-gray-600"
                                          colSpan={3}
                                        >
                                          {renderDetailChart(
                                            kpiRow,
                                            false,
                                            selectedRow.kpi,
                                            tempData,
                                            compareCompanyView,
                                            companyCount,
                                          )}
                                        </td>
                                        {compareCompanyView && (
                                          <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300 align-top max-w-[250px]">
                                            <div className="whitespace-pre-wrap break-words">
                                              {!compareCompanyView
                                                ? "-"
                                                : (() => {
                                                    const key = `${lever}-${selectedRow.kpi}`;
                                                    const obs =
                                                      observations[key];
                                                    const loading =
                                                      loadingObservations[key];

                                                    if (!obs && !loading) {
                                                      const company =
                                                        getCompanyName();
                                                      const value =
                                                        getTempCalculatedValue(
                                                          selectedRow.kpi,
                                                        );
                                                      const p25 =
                                                        parseFloat(
                                                          kpiRow?.[
                                                            "25th Percentile"
                                                          ],
                                                        ) || null;
                                                      const p50 =
                                                        parseFloat(
                                                          kpiRow?.[
                                                            "50th Percentile"
                                                          ],
                                                        ) || null;
                                                      const p75 =
                                                        parseFloat(
                                                          kpiRow?.[
                                                            "75th Percentile"
                                                          ],
                                                        ) || null;
                                                      setTimeout(
                                                        () =>
                                                          generateObservation(
                                                            company,
                                                            lever,
                                                            selectedRow.kpi,
                                                            value,
                                                            p25,
                                                            p50,
                                                            p75,
                                                          ),
                                                        0,
                                                      );
                                                    }

                                                    return loading
                                                      ? "Generating..."
                                                      : obs || "-";
                                                  })()}
                                            </div>
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  },
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HorizontalBenchmarkChart;
